import { Button } from "@/components/ui/button";
import {
    Form,
} from "@/components/ui/form";
import { usePriorityEditPermission } from "@/hooks/usePriorityEditPermission";
import { getISOWeek, cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Priority, PriorityStatus, PriorityType } from "@/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { prioritySchema, type PriorityFormValues } from "./form/priority-form-schema";
import type { SubmitIntent } from "./form/priority-form-constants";
import { submitLabels } from "./priorityUtils";
import { PriorityFormCreateView } from "./form/PriorityFormCreateView";
import { assignmentsFromPriority } from "./form/priority-form-transforms";
import { usePriorityFormData } from "./form/usePriorityFormData";
import { usePriorityFormSubmit } from "./form/usePriorityFormSubmit";

interface PriorityFormProps {
    initialData?: Partial<Priority>;
    onSuccess?: () => void;
    onCancel?: () => void;
    hideButtons?: boolean;
    /** Parent-driven submit; `intent` "commit" enforces the DRI + PPP gate. */
    onSubmitRef?: (submitFn: (intent?: SubmitIntent) => void) => void;
    onSubmittingChange?: (isSubmitting: boolean) => void;
    /**
     * Fires after a successful save when the current user's own row moved from `focus` to
     * `blocked`/`waiting` — the moment a Queue→Focus promotion prompt makes sense. Only the caller's
     * own row is checked, so editing someone else's status never triggers it.
     */
    onOwnFocusStalled?: (userPid: string) => void;
}

export function PriorityForm({
    initialData,
    onSuccess,
    onCancel,
    hideButtons = false,
    onSubmitRef,
    onSubmittingChange,
    onOwnFocusStalled,
}: PriorityFormProps) {
    const { user } = useAuth();
    const isEditingExisting = Boolean(initialData?.code);
    // Task-level fields belong to the DRI, with creator and admin as break-glass — those go read-only
    // for everyone else, since the backend refuses the whole PUT. But an assignee still owns their own
    // row: it stays editable and saves through PATCH /assignments/me instead of the task-level PUT.
    const { canEditTask, canEditOwnRow, driName, isDriMissing } = usePriorityEditPermission(initialData);
    // Own-row mode: the user may not touch the task, but has a row of their own to edit.
    const isOwnRowOnly = isEditingExisting && !canEditTask && canEditOwnRow;

    const defaultDate = new Date();
    const { week, year } = getISOWeek(defaultDate);

    const form = useForm<PriorityFormValues>({
        resolver: zodResolver(prioritySchema),
        defaultValues: {
            name: initialData?.name || "",
            type: (initialData?.type || "SHP") as PriorityType,
            description: initialData?.description || "",
            weekNumber: initialData?.weekNumber ?? week,
            weekYear: initialData?.weekYear ?? year,
            assignments: isEditingExisting
                ? assignmentsFromPriority(initialData)
                : user?.pid
                ? [{ userPid: user.pid, status: "queue" as PriorityStatus, icTask: "", isDri: true, blockerUserPid: undefined, blockedReason: undefined }]
                : [],
            status: initialData?.status || "queue",
            isConfidential: initialData?.isConfidential ?? false,
            pppPids: initialData?.ppps?.map((p) => p.pid) ?? [],
        },
    });

    const { fields: assignmentFields, replace: replaceAssignments } = useFieldArray({
        control: form.control,
        name: "assignments",
    });

    const assignedUserPids = useMemo(() => assignmentFields.map((f) => f.userPid), [assignmentFields]);
    const { users, isLoadingUsers, userSearch, setUserSearch, loadByPid } = usePriorityFormData(
        assignedUserPids,
        initialData?.pid
    );

    const onAssigneePidsChange = useCallback(
        (pids: string[]) => {
            const prev = form.getValues("assignments");
            const byPid = new Map(prev.map((r) => [r.userPid, r]));
            replaceAssignments(
                pids.map((pid) => {
                    const existing = byPid.get(pid);
                    if (existing) return existing;
                    return {
                        userPid: pid,
                        status: "queue" as PriorityStatus,
                        icTask: "",
                        isDri: false,
                        blockerUserPid: undefined,
                        blockedReason: undefined,
                    };
                })
            );
        },
        [form, replaceAssignments]
    );

    /** Toggle isDri on one assignment; clears isDri on all others (radio behaviour) */
    const onSetDri = useCallback(
        (driPid: string) => {
            const current = form.getValues("assignments");
            replaceAssignments(current.map((a) => ({ ...a, isDri: a.userPid === driPid })));
        },
        [form, replaceAssignments]
    );

    const { mutation, onSubmit } = usePriorityFormSubmit({
        form,
        initialData,
        isOwnRowOnly,
        canEditTask,
        canEditOwnRow,
        driName,
        week,
        year,
        onSubmitRef,
        onSubmittingChange,
        onSuccess,
        onOwnFocusStalled,
    });

    const labels = submitLabels(initialData as Priority | undefined, isOwnRowOnly);
    const secondary = labels.secondary;

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit((v) => onSubmit(v, labels.primary.intent))}
                className={cn("w-full pb-4 pt-1", isEditingExisting && "pt-2")}
            >
                <PriorityFormCreateView
                    mode={isEditingExisting ? "edit" : "create"}
                    priorityCode={initialData?.code}
                    initialData={isEditingExisting ? initialData : undefined}
                    assignmentFields={assignmentFields}
                    users={users}
                    isLoadingUsers={isLoadingUsers}
                    userSearch={userSearch}
                    setUserSearch={setUserSearch}
                    onAssigneePidsChange={onAssigneePidsChange}
                    onSetDri={onSetDri}
                    loadByPid={loadByPid}
                    readOnly={!canEditTask}
                    canEditOwnRow={canEditOwnRow}
                    driName={driName}
                    isDriMissing={isDriMissing}
                />

                {!hideButtons && (
                    <div className="col-span-full mt-2 flex justify-end gap-3 border-t pt-6">
                        <Button type="button" variant="outline" onClick={onCancel}>
                            Cancel
                        </Button>
                        {secondary && (
                            <Button type="button" variant="secondary" disabled={mutation.isPending} onClick={() => form.handleSubmit((v) => onSubmit(v, secondary.intent))()}>
                                {secondary.label}
                            </Button>
                        )}
                        <Button type="button" disabled={mutation.isPending || (!canEditTask && !canEditOwnRow)} onClick={() => form.handleSubmit((v) => onSubmit(v, labels.primary.intent))()}>
                            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {labels.primary.label}
                        </Button>
                    </div>
                )}
            </form>
        </Form>
    );
}
